import { useRef, useEffect, useState, ReactNode, Children } from 'react';

interface Props {
  children: ReactNode;
  className?: string;
  baseDelay?: number;
  staggerDelay?: number;
  direction?: 'up' | 'left' | 'right';
}

export default function StaggerReveal({
  children,
  className = '',
  baseDelay = 0,
  staggerDelay = 110,
  direction = 'up',
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const getHiddenTransform = (): string => {
    switch (direction) {
      case 'left': return 'translateX(-40px)';
      case 'right': return 'translateX(40px)';
      default: return 'translateY(40px)';
    }
  };

  const childArray = Children.toArray(children);

  return (
    <div ref={ref} className={className}>
      {childArray.map((child, i) => {
        const delay = baseDelay + i * staggerDelay;
        return (
          <div
            key={i}
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translate(0, 0)' : getHiddenTransform(),
              // No blur — CSS blur on 40+ simultaneous elements kills GPU
              transition: visible
                ? `opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`
                : 'none',
              height: '100%',
            }}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
}
